import { Button as ButtonPrimitive } from "@base-ui/react/button";
import type { ComponentProps, ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const SECONDARY =
  "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_6%)] aria-expanded:bg-secondary";

/**
 * THE button. Every clickable action in Aura Core is one of these. If a screen needs a look this file
 * does not offer, add the variant here; never override colour, height or radius at the call site
 * (test/uiGuardrails.test.ts enforces that).
 *
 *   default      the one primary action on a surface — Aura green
 *   outline      secondary actions (Edit, View document, Cancel)
 *   secondary    a quiet filled action
 *   ghost        icon buttons and row actions
 *   link         an action that reads as text
 *   destructive  a confirmed destructive action (Withdraw, Cancel booking)
 *   destructive-quiet  a secondary destructive action beside others (Cancel visit, Not going ahead, Remove a file, Discard):
 *                outlined like `outline`, red type; it opens the confirm whose own button is `destructive`
 *   warning      proceeding at risk
 *   toggle       on/off, said by aria-pressed
 *   inverse      an action on a `feature` card: white, with deep-green type
 *   bare         a title that opens something: foreground text, no box, underline on hover
 *   nav          one entry of a list of places (the Help chapters): ghost, with the one you are on said by aria-current
 *   tile         a choice that carries two lines (a free assessor in a booking slot, an administrator
 *                on the New job wizard). Bordered and full width at rest; the chosen one says so by aria-pressed
 *
 *   float        a quiet filled button laid over a picture (a photo's arrows, a thumbnail's remove): lifted off it by a shadow
 *
 * Heights: sm 32 · default 40 · lg 44 (xs 28 for dense rows only; it grows to 36 on touch). field 40, with the narrow sides of a
 * text box: a button that takes a form field's place (the short date).
 * hero: 64 on a phone, lg (44) from sm up — the one action a phone page is for. At most one per screen.
 * launcher: the round floating button that opens a panel from a corner of every page (the feedback
 * button). 48, a circle, lifted off the page by a shadow. One per page, owned by the shell.
 */
const buttonVariants = cva(
  "group/button inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg border border-transparent bg-clip-padding font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-foreground/70 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-35 disabled:saturate-0 aria-invalid:border-destructive aria-invalid:focus-visible:outline-2 aria-invalid:focus-visible:outline-solid aria-invalid:focus-visible:outline-offset-2 aria-invalid:focus-visible:outline-destructive/70 dark:aria-invalid:border-warning dark:aria-invalid:focus-visible:outline-warning/70 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-hover",
        outline:
          "border-border bg-card text-foreground hover:border-primary/40 hover:bg-primary/5 aria-expanded:bg-muted dark:bg-foreground/5 dark:hover:bg-foreground/8",
        secondary: SECONDARY,
        float: `${SECONDARY} shadow-sm`,
        ghost:
          "text-muted-foreground hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:border-destructive/40 focus-visible:ring-destructive/20",
        "destructive-quiet":
          "border-border bg-card text-destructive hover:border-destructive/40 hover:bg-destructive/5 aria-expanded:bg-destructive/5 dark:bg-foreground/5 dark:hover:bg-destructive/10",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 focus-visible:ring-warning/30",
        link: "h-auto px-0 text-primary-hover underline-offset-4 hover:underline dark:text-primary",
        // On or off, said by `aria-pressed`: off is a quiet outline, on takes the "ready" tokens. Never colour one by hand.
        toggle:
          "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground aria-pressed:border-readiness-ready-edge aria-pressed:bg-readiness-ready-bg aria-pressed:text-readiness-ready-fg dark:bg-foreground/5",
        nav: "text-muted-foreground hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground aria-current:bg-accent aria-current:text-aura-deep dark:hover:bg-muted/50 dark:aria-current:text-foreground",
        inverse: "bg-white text-aura-deep hover:bg-white/90",
        bare: "justify-start text-left whitespace-normal text-foreground hover:underline",
        tile: "w-full justify-between border-border bg-card text-left font-normal whitespace-normal text-foreground hover:border-primary/60 hover:bg-primary/5 aria-pressed:border-primary aria-pressed:bg-primary/5 dark:bg-foreground/5 dark:hover:border-primary/60 dark:aria-pressed:border-primary",
      },
      size: {
        default: "h-10 gap-2 px-5 text-body has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        xs: "h-7 gap-1 px-2.5 text-caption pointer-coarse:h-9 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 gap-1.5 px-3 text-small has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-3.5",
        field: "h-10 gap-2 px-3 text-body",
        lg: "h-11 gap-2 px-6 text-body has-data-[icon=inline-end]:pr-5 has-data-[icon=inline-start]:pl-5",
        hero: "h-16 gap-3 px-6 text-heading has-data-[icon=inline-end]:pr-5 has-data-[icon=inline-start]:pl-5 [&_svg:not([class*='size-'])]:size-5 sm:h-11 sm:gap-2 sm:text-body sm:[&_svg:not([class*='size-'])]:size-4",
        icon: "size-10",
        "icon-xs": "size-7 pointer-coarse:size-9 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "size-8",
        "icon-lg": "size-11",
        launcher: "size-12 rounded-full shadow-lg [&_svg:not([class*='size-'])]:size-5",
      },
    },
    // A link reads as text at any size: the size sets its font, never a box around it.
    compoundVariants: [
      { variant: "link", className: "h-auto px-0 has-data-[icon=inline-end]:pr-0 has-data-[icon=inline-start]:pl-0" },
      {
        variant: "bare",
        className: "h-auto gap-1 px-0 has-data-[icon=inline-end]:pr-0 has-data-[icon=inline-start]:pl-0",
      },
      // A tile is as tall as its two lines, at any size.
      { variant: "tile", className: "h-auto gap-2 px-2.5 py-2 active:not-aria-[haspopup]:translate-y-0" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return <ButtonPrimitive data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

/**
 * A button that goes to an address or downloads a file: the same Button, drawn on an `<a>` that holds its words (a download,
 * a document in a new tab). It keeps the Button's look and keyboard behaviour; `href` is required.
 */
function ButtonLink({
  href,
  download,
  target,
  rel,
  children,
  ...props
}: Omit<ButtonPrimitive.Props, "render" | "nativeButton" | "children"> &
  VariantProps<typeof buttonVariants> &
  Pick<ComponentProps<"a">, "download" | "target" | "rel"> & { href: string; children: ReactNode }) {
  return (
    <Button
      nativeButton={false}
      render={
        <a href={href} download={download} target={target} rel={rel}>
          {children}
        </a>
      }
      {...props}
    />
  );
}

export { Button, ButtonLink, buttonVariants };
